import React, { useCallback } from "react";
import { Badge } from "@teals/ui-web";
import {
  StepContent,
  InfoBanner,
  InfoBannerIcon,
  ErrorBanner,
  LoadingContainer,
  LoadingText,
  ThinkingDots,
  ToggleSwitch,
  ToggleSlider,
} from "../styles";
import type { SchemaField } from "../../../store/api/dataSourcesApi";

interface StepAIAttributesProps {
  fields: SchemaField[];
  onFieldsChange: (fields: SchemaField[]) => void;
  isAnalyzing: boolean;
  analyzeError: string | null;
}

const StepAIAttributes: React.FC<StepAIAttributesProps> = ({
  fields,
  onFieldsChange,
  isAnalyzing,
  analyzeError,
}) => {
  const handleToggle = useCallback(
    (index: number, attr: "searchable" | "indexable") => {
      const updated = fields.map((f, i) =>
        i === index ? { ...f, [attr]: !f[attr] } : f,
      );
      onFieldsChange(updated);
    },
    [fields, onFieldsChange],
  );

  if (isAnalyzing) {
    return (
      <StepContent>
        <LoadingContainer>
          <ThinkingDots>
            <span />
            <span />
            <span />
          </ThinkingDots>
          <LoadingText>Analyzing schema with AI\u2026</LoadingText>
        </LoadingContainer>
      </StepContent>
    );
  }

  return (
    <StepContent>
      {analyzeError && <ErrorBanner>{analyzeError}</ErrorBanner>}
      <InfoBanner>
        <InfoBannerIcon>{"\u2728"}</InfoBannerIcon>
        AI has analyzed your schema and suggested field attributes. These
        suggestions are based on field names and types — you can adjust them
        before creating.
      </InfoBanner>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "0.5rem" }}>Field Name</th>
            <th style={{ textAlign: "left", padding: "0.5rem" }}>Type</th>
            <th style={{ textAlign: "center", padding: "0.5rem" }}>
              Searchable
            </th>
            <th style={{ textAlign: "center", padding: "0.5rem" }}>
              Indexable
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, idx) => (
            <tr key={field.name}>
              <td style={{ padding: "0.5rem", fontFamily: "monospace" }}>
                {field.name}
              </td>
              <td style={{ padding: "0.5rem" }}>
                <Badge variant="neutral">{field.type}</Badge>
              </td>
              <td style={{ padding: "0.5rem", textAlign: "center" }}>
                <ToggleSwitch onClick={() => handleToggle(idx, "searchable")}>
                  <input type="checkbox" checked={field.searchable} readOnly />
                  <ToggleSlider $checked={field.searchable} />
                </ToggleSwitch>
              </td>
              <td style={{ padding: "0.5rem", textAlign: "center" }}>
                <ToggleSwitch onClick={() => handleToggle(idx, "indexable")}>
                  <input type="checkbox" checked={field.indexable} readOnly />
                  <ToggleSlider $checked={field.indexable} />
                </ToggleSwitch>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </StepContent>
  );
};

export default StepAIAttributes;
